import { ApiError } from '@eneco/dc/client/ApiError';
import { getOfferProductsV3 } from '@eneco/dc/services/ProductsPublicService';
import { MachineConfig } from '@eneco/flows/types';
import {
  DC_Domain_Models_Products_OfferType,
  Products_Offers_V3_OfferResponseModel,
  RequestModels_Products_Offers_V3_OfferProductsPublicV3Request,
  ResponseModels_Products_Offers_V3_OfferResponseModel,
} from '@monorepo-types/dc';
import { assign, enqueueActions, fromPromise, setup } from 'xstate';

import { FlowContextServiceGemak } from '../context';

export interface OfferMachineContext {
  offer?: Products_Offers_V3_OfferResponseModel;
  errorMessage?: string;
}

export type OfferEvent =
  | {
      type: 'GET_OFFER';
      config: MachineConfig;
      values: FlowContextServiceGemak;
      offerType: DC_Domain_Models_Products_OfferType;
    }
  | { type: 'IDLE' }
  | { type: 'NOT_FOUND' }
  | { type: 'NOT_AVAILABLE' }
  | { type: 'NOT_AUTHENTICATED' }
  | { type: 'NOT_SUPPORTED' };

const filterEventValues = (values?: FlowContextServiceGemak): FlowContextServiceGemak => {
  return ['postalCode', 'houseNumber', 'houseNumberSuffix', 'voucherCode', 'startDate'].reduce(
    (obj, key) => ({ ...obj, [key]: values?.[key as keyof FlowContextServiceGemak] }),
    {},
  );
};

const hasUnsupportedProducts = (data?: Products_Offers_V3_OfferResponseModel): boolean =>
  !!data?.products?.some(product => !!product?.productType?.includes('warmthProducts'));

const getOfferService = (
  config: MachineConfig,
  values: FlowContextServiceGemak,
  offerType: DC_Domain_Models_Products_OfferType,
): Promise<OutputType> =>
  new Promise((resolve, reject) => {
    const requestBody: RequestModels_Products_Offers_V3_OfferProductsPublicV3Request = {
      data: {
        // Required Non-Com values
        offerType,
        address: {
          postalCode: values.postalCode?.replace(/\s/g, ''),
          houseNumber: values.houseNumber ?? 0,
          ...(values.houseNumberSuffix &&
            values.houseNumberSuffix !== '-' && { houseNumberSuffix: values.houseNumberSuffix }),
        },
        voucherCode: values.voucherCode,
        products: ['servicegemak'],

        // Required values below set to 0 for request purposes
        usages: {
          electricityNormal: 0,
          electricityLow: 0,
          electricityRedeliveryNormal: 0,
          electricityRedeliveryLow: 0,
          warmthElectricity: 0,
          warmthWater: 0,
          gas: 0,
        },
        isMkb: false,
        includeRentalProducts: false,
        urgent: false,
        movingCustomer: false,
        preferredContractStartDate: values.startDate,
        allowDynamicOrHybridOnly: false,
      },
    };

    return getOfferProductsV3({
      businessUnit: config.businessUnit,
      label: config.label,
      requestBody,
    })
      .then(response => {
        resolve({ data: response, values, offerType });
      })
      .catch((error: ApiError) => {
        let message = error?.message;

        // check for a specific "Sales Cap" guid value
        if (message === 'Bad Request' && error.firstError?.code?.includes('f8bdd982-6924-4daf-906c-ea976beff26c')) {
          message = 'Sales Cap Reached';
        }

        reject(new Error(message));
      });
  });
interface InputType {
  event: OfferEvent;
  context: OfferMachineContext;
}

interface OutputType {
  data?: ResponseModels_Products_Offers_V3_OfferResponseModel;
  values?: FlowContextServiceGemak;
  offerType: DC_Domain_Models_Products_OfferType;
}

export const offerMachineNonCommodity = setup({
  types: {} as {
    context: OfferMachineContext;
    events: OfferEvent;
    actors: {
      fetchData: {
        input: InputType;
        output: OutputType;
      };
    };
  },
  actions: {
    clearErrorMessage: assign({
      errorMessage: undefined,
    }),
  },
  actors: {
    fetchData: fromPromise<OutputType, InputType>(({ input }) => {
      const { event } = input;
      if (event.type !== 'GET_OFFER') {
        throw new Error('Invalid event type');
      }

      const { config, values, offerType } = event;

      const filteredValues = filterEventValues(values);

      return getOfferService(config, filteredValues, offerType);
    }),
  },
}).createMachine({
  id: 'offer',
  initial: 'IDLE',
  context: {},
  on: {
    GET_OFFER: { target: '.FETCHING' },
    IDLE: { target: '.IDLE' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'fetchData',
        input: ({ event, context }) => ({ event, context }),
        onDone: {
          target: 'SUCCESS',
          actions: [
            enqueueActions(({ enqueue, event }) => {
              if (hasUnsupportedProducts(event.output?.data?.data)) {
                enqueue.raise({ type: 'NOT_SUPPORTED' });
              }
            }),
            assign({
              offer: ({ event }) => event.output?.data?.data,
            }),
          ],
        },
        onError: {
          target: 'ERROR',
          actions: [
            enqueueActions(({ enqueue, event }) => {
              const error = event.error as Error;
              if (error.message === 'Sales Cap Reached' || error.message === 'Not Found') {
                enqueue.raise({ type: 'NOT_AVAILABLE' });
              }
              if (error.message === 'Unauthorized') {
                enqueue.raise({ type: 'NOT_AUTHENTICATED' });
              }
            }),
            assign({
              errorMessage: ({ event }) => (event.error as Error).message,
            }),
          ],
        },
      },
    },
    SUCCESS: {
      on: {
        NOT_SUPPORTED: { target: 'ERROR_NOT_SUPPORTED' },
      },
      entry: 'clearErrorMessage',
      after: {
        500: { target: 'IDLE' },
      },
    },
    ERROR: {
      on: {
        NOT_FOUND: { target: 'ERROR_NOT_FOUND' },
        NOT_AVAILABLE: { target: 'ERROR_NOT_AVAILABLE' },
        NOT_AUTHENTICATED: { target: 'ERROR_NOT_AUTHENTICATED' },
      },
    },
    ERROR_NOT_SUPPORTED: {},
    ERROR_NOT_FOUND: {},
    ERROR_NOT_AVAILABLE: {},
    ERROR_NOT_AUTHENTICATED: {},
  },
});
